// Client-added Font Awesome animations: play on hover, finish the current cycle
// after the pointer leaves (instead of stopping mid-animation), then rest.
// Pairs with webroot/sass/general/_fa-hover-animations.scss (.ss-fa-playing).

const ANIMATION_CLASSES: readonly string[] = [
    'fa-beat',
    'fa-fade',
    'fa-beat-fade',
    'fa-bounce',
    'fa-flip',
    'fa-shake',
    'fa-float',
    'fa-jello',
    'fa-swing',
    'fa-wag',
    'fa-buzz',
    'fa-spin',
    'fa-spin-snap',
    'fa-spin-pulse',
    'fa-pulse',
];
const ANIMATION_NAMES: ReadonlySet<string> = new Set(ANIMATION_CLASSES);
const ICON_SELECTOR: string = ANIMATION_CLASSES.map((name) => `.${name}`).join(', ');
const PLAY_CLASS: string = 'ss-fa-playing';
const CONTAINER_ID: string = 'page-content-wrapper';

const initFaHoverAnimations = (): void => {
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    scanForIcons(container);
    observeAddedIcons(container);
};

const observeAddedIcons = (container: HTMLElement): void => {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (node.matches(ICON_SELECTOR)) enhanceIcon(node);
                scanForIcons(node);
            });
        });
    });
    observer.observe(container, { childList: true, subtree: true });
};

const scanForIcons = (root: ParentNode): void => {
    root.querySelectorAll<HTMLElement>(ICON_SELECTOR).forEach(enhanceIcon);
};

const enhanceIcon = (icon: HTMLElement): void => {
    if (icon.dataset.faHoverBound) return;
    icon.dataset.faHoverBound = '1';

    const hoverZone: HTMLElement = icon.closest<HTMLElement>('a, button, li') ?? icon;

    // The icon is "engaged" while the pointer is over it or it holds keyboard
    // focus. CSS drives the animation from :hover / :focus-within; these flags
    // let the play-class bridge below know when neither is active anymore.
    const isEngaged = (): boolean => icon.dataset.faHover === '1' || icon.dataset.faFocus === '1';

    const activate = (): void => {
        icon.dataset.faRunning = '1';
        icon.classList.remove(PLAY_CLASS);
    };

    // CSS plays a single cycle (--fa-animation-iteration-count: 1). If the
    // pointer/focus leaves while the cycle is still running, add the play class
    // to bridge past the end of :hover / :focus-within so the cycle finishes.
    // Once it ends -- or if it already finished while still engaged -- the class
    // is dropped so animation-name returns to none and the next engage replays it.
    const deactivate = (): void => {
        if (isEngaged()) return;
        if (icon.dataset.faRunning === '1' && !prefersReducedMotion()) {
            icon.classList.add(PLAY_CLASS);
        } else {
            icon.classList.remove(PLAY_CLASS);
        }
    };

    hoverZone.addEventListener('mouseenter', () => {
        icon.dataset.faHover = '1';
        activate();
    });

    hoverZone.addEventListener('mouseleave', () => {
        icon.dataset.faHover = '0';
        deactivate();
    });

    // focusin/focusout bubble, so binding on the hover zone also catches focus
    // moving to a focusable descendant (e.g. the icon's containing link/button).
    hoverZone.addEventListener('focusin', () => {
        icon.dataset.faFocus = '1';
        activate();
    });

    hoverZone.addEventListener('focusout', () => {
        icon.dataset.faFocus = '0';
        deactivate();
    });

    // Only fires for multi-cycle animations (e.g. client custom classes that set
    // their own iteration count); harmless for the single-cycle default.
    icon.addEventListener('animationiteration', (event: AnimationEvent) => {
        if (ANIMATION_NAMES.has(event.animationName) && !isEngaged()) {
            icon.classList.remove(PLAY_CLASS);
        }
    });

    icon.addEventListener('animationend', (event: AnimationEvent) => {
        if (ANIMATION_NAMES.has(event.animationName)) {
            icon.dataset.faRunning = '0';
            icon.classList.remove(PLAY_CLASS);
        }
    });
};

const prefersReducedMotion = (): boolean =>
    Boolean(window.matchMedia) && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFaHoverAnimations);
} else {
    initFaHoverAnimations();
}
